import { LoaderCircle } from 'lucide-react';
import type { SavedCard } from '../../../api/dispatch';
import type { PaymentMode } from './bookingForm';

/**
 * How the job gets paid. dispatch-be refuses a non-quote job that carries no
 * payment method at all (`commonApis/jobcreate.js`), so this is not an optional
 * extra on the form — without a choice here "Save as job" is a guaranteed 400.
 *
 * No add-a-card sheet: collecting card details is dispatch's job, and a booking
 * drafted out of a chat should never be the place an agent types a PAN. When
 * there is nothing on file the honest options are a payment link or bill later.
 */

export type CardLookup =
  /** No dispatch customer to look up — a guest booking. */
  | { status: 'guest' }
  | { status: 'loading' }
  | { status: 'ready'; cards: SavedCard[] }
  /** Unreachable, or the agent lacks dispatch's `customer_payment_cards`. */
  | { status: 'error'; message: string };

const MONTH = (month: number | null) => (month ? String(month).padStart(2, '0') : '');

const cardLabel = (card: SavedCard) => {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  const number = card.last4 ? `···· ${card.last4}` : '';
  const expiry = card.expYear ? `exp ${MONTH(card.expMonth)}/${String(card.expYear).slice(-2)}` : '';
  return [brand, number, expiry].filter(Boolean).join(' · ');
};

export function PaymentSection({
  mode,
  methodId,
  lookup,
  disabled,
  onChange,
}: {
  mode: PaymentMode;
  methodId: string;
  lookup: CardLookup;
  disabled: boolean;
  onChange: (mode: PaymentMode, methodId: string) => void;
}) {
  const cards = lookup.status === 'ready' ? lookup.cards : [];
  // The three no-card states differ to the agent: a guest has nowhere to keep a
  // card, a known customer simply has none yet, and a refused lookup means the
  // customer may well have one this agent is not allowed to see.
  const note =
    lookup.status === 'error'
      ? `Saved cards unavailable — ${lookup.message}`
      : lookup.status === 'guest'
        ? 'Booking as a guest, so there is no card on file.'
        : lookup.status === 'ready' && !cards.length
          ? 'This customer has no saved card in dispatch.'
          : null;

  return (
    <div className="aib-pay">
      <div className="aib-pay-head">
        <span className="aib-label">Payment</span>
        <span className="aib-hint">Applies to a job — quotes and drafts ignore it.</span>
      </div>

      {lookup.status === 'loading' ? (
        <div className="aib-pay-busy">
          <LoaderCircle size={12} className="spin-anim" /> Checking saved cards…
        </div>
      ) : (
        <>
          {note && <p className="aib-pay-note">{note}</p>}
          <div className="aib-pay-opts">
            {cards.map((card) => (
              <label key={card.id} className="aib-pay-opt">
                <input
                  type="radio"
                  name="aib-payment"
                  disabled={disabled}
                  checked={mode === 'card' && methodId === card.id}
                  onChange={() => onChange('card', card.id)}
                />
                <span>{cardLabel(card)}</span>
              </label>
            ))}
            <label className="aib-pay-opt">
              <input
                type="radio"
                name="aib-payment"
                disabled={disabled}
                checked={mode === 'paymentLink'}
                onChange={() => onChange('paymentLink', '')}
              />
              <span>Send a payment link</span>
            </label>
            <label className="aib-pay-opt">
              <input
                type="radio"
                name="aib-payment"
                disabled={disabled}
                checked={mode === 'billLater'}
                onChange={() => onChange('billLater', '')}
              />
              {/* Same wire value either way; the wording is what changes, because
                  skipping a card you were shown and booking without one you never
                  had are different decisions for the agent. */}
              <span>{cards.length ? 'Skip — bill later' : 'Book anyway — bill later'}</span>
            </label>
          </div>
        </>
      )}
    </div>
  );
}
